import React, { useEffect, useState } from "react";
import { Form, Grid, Popup, Header, Segment } from "semantic-ui-react";
import {
    Button,
    Icon,
    Notification,
    SettingsFooter,
    STATUS_TYPES,
    yup,
    generateResolver,
    USER_PERMISSIONS
} from "dyl-components";
import DataTable from "./DataTable";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import users from "actions/users";
import { useParams } from "react-router-dom";
import { MathUtils } from "utils";
import useWindowWidth from "shared/SettingsFooter/useWindowWidth";
import useWidthListener from "shared/SettingsFooter/useWidthListener";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";
import { useBlocker } from "shared/confirmation/UseBlocker";
import RestrictedView from 'shared/RestrictedView';

const WebLauncher = () => {
    const dispatch = useDispatch();
    const { isReadingLaunchUrl, launchUrls, isUpdaingLaunchUrl } = useSelector(
        (state) => state.users,
    );
    const { id: userId } = useParams();
    const {
        control,
        formState: { isValid, isDirty },
        getValues,
        handleSubmit,
        reset,
        watch,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            urls: launchUrls,
        },
        resolver: generateResolver({
            urls: yup.array().of(
                yup.object().shape({
                    name: yup.string().trim().required("Name is required"),
                    url: yup.string().matches(/^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?(\?[^\s]*)?$/, 'Must be a valid URL').required("URL is required"),
                }),
            ),
        }),
    });

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: "urls",
    });
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [editingRows, setEditingRows] = useState(null);
    const isRowEditMode =
        editingRows &&
        Object.values(editingRows)?.some?.((value) => value === true);

    const windowWidth = useWindowWidth();
    const width = useWidthListener("settingsSidebar");

    const onSubmit = async (values) => {
        setEditingRows(null);
        await dispatch(users.setLaunchUrl(userId, values?.urls))
            .then((res) => {
                if (res === "Ok") {
                    Notification.alert(
                        "Successfully updated the URL",
                        STATUS_TYPES.SUCCESS,
                    );
                    dispatch(users.getLaunchUrl(userId));
                } else {
                    Notification.alert(
                        "Failed to update the URL",
                        STATUS_TYPES.ERROR,
                    );
                }
            })
            .catch((err) => {
                console.log(err);
                Notification.alert(
                    "Failed to update the URL",
                    STATUS_TYPES.ERROR,
                );
            });
    };

    const onDeleteConfirm = (index) => {
        remove?.(index);
    };

    const cancelEdit = () => {
        if (isDirty || isRowEditMode) {
            reset();
            setEditingRows(null);
        }
    };

    const handleConfirmLeave = () => {
        setIsConfirmationOpen(false);
        cancelEdit();
        if (pendingNavigation) {
            pendingNavigation.retry();
        }
    };

    useBlocker((tx) => {
        if (isDirty && !isConfirmationOpen) {
            setPendingNavigation(tx);
            setIsConfirmationOpen(true);
        } else {
            tx.retry();
        }
    }, isDirty);

    useEffect(() => {
        dispatch(users.getLaunchUrl(userId));
    }, [dispatch, userId]);

    useEffect(() => {
        reset({
            urls: launchUrls,
        });
    }, [launchUrls, reset]);

    return (
        <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_VIEW} type="restricted" userRestrict>
            <div className="WebLauncher">
                <Segment basic>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={15}>
                                <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_ADD} type="disabled" userRestrict>
                                    <Header
                                        // as="h4"
                                        color="primary"
                                        size='medium'
                                        disabled={isReadingLaunchUrl || fields?.length === 3 || isRowEditMode}
                                    >
                                        <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_ADD} type="disabled" userRestrict>
                                            <Icon
                                                style={{cursor: 'pointer'}}
                                                onClick={() => {
                                                    append({
                                                        name: "",
                                                        url: "",
                                                    }, { shouldFocus: false });

                                                    setEditingRows((pre) => ({
                                                        ...pre,
                                                        [fields.length]: true,
                                                    }));
                                                }}
                                                disabled={isReadingLaunchUrl || fields?.length === 3 || isRowEditMode}
                                                size="large"
                                                className="plus circle"
                                            />
                                        </RestrictedView>
                                        <Header.Content>Web Launcher</Header.Content>
                                    </Header>
                                </RestrictedView>
                            </Grid.Column>
                            <Grid.Column width={1}>
                                <Popup
                                    trigger={<Icon className="fas fa-circle-info" color='blue' size='large'/>}
                                    content={
                                        <span>
                                            Uses the URL to find and open the caller’s
                                            matching profile. Disable any popup blockers for best performance. Url must include %%phone%% for
                                            number lookup when a call is answered. Ex:
                                            https://my.dyl.com/user/search?q=%%phone%%
                                        </span>
                                    }
                                    style={{maxWidth: 500}}
                                    inverted
                                    hoverable
                                    position='left center'
                                />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
                <Form
                    loading={isReadingLaunchUrl}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <DataTable
                        fields={fields}
                        control={control}
                        update={update}
                        getValues={getValues}
                        editingRows={editingRows}
                        setEditingRows={setEditingRows}
                        remove={remove}
                        onDeleteConfirm={onDeleteConfirm}
                        watch={watch}
                    />
                    <SettingsFooter
                        style={{
                            width: MathUtils.calculatePercentage(
                                windowWidth,
                                windowWidth - width,
                            ),
                        }}
                        className={`Webform__menu`}
                        rightOptions={[
                            <Button
                                onClick={() => {
                                    isDirty
                                        ? setIsConfirmationOpen(true)
                                        : cancelEdit();
                                }}
                                status={"cancel"}
                                basic
                                type="button"
                            >
                                Cancel
                            </Button>,

                            <Button
                                disabled={
                                    !isValid ||
                                    !isDirty ||
                                    isRowEditMode ||
                                    isUpdaingLaunchUrl
                                }
                                loading={isReadingLaunchUrl || isUpdaingLaunchUrl}
                                type="submit"
                            >
                                Save
                            </Button>,
                        ]}
                    />
                </Form>
                <FullScreenModalPopUp
                    header={"Changes not saved"}
                    subheader={"Are you sure you want to exit?"}
                    isOpen={isConfirmationOpen}
                    onConfirm={handleConfirmLeave}
                    onFormClose={() => setIsConfirmationOpen(false)}
                    closeIcon={false}
                />
            </div>
        </RestrictedView>
    );
};

export default WebLauncher;
